/**
 * d-collection-sort-availability.js
 *
 * Ordena os produtos da coleção colocando disponíveis antes dos esgotados,
 * buscando todas as páginas da coleção e construindo uma paginação virtual
 * no cliente (sem recarregar a página).
 *
 * Adaptado para o tema Lola (não-Dawn):
 *   - Grid: div#product-grid-ajax (filhos são <div class="card ...">, não <li>)
 *   - Container observado: div#collection-ajax (data-ajax-parent)
 *   - Paginação: div#pagination-ajax, reaproveitando classes .pagination /
 *     .pagination-item / .arrow-link / .arrow-link--prev / .arrow-link--next
 *   - component-facets.js (filtros/sort AJAX) só troca o innerHTML de
 *     #product-grid-ajax — os data-attributes do próprio elemento (per-page,
 *     total-pages) NÃO são atualizados. Por isso, a cada (re)fetch, este
 *     script busca a página 1 fresca e lê o data-total-pages de lá antes de
 *     decidir quantas páginas buscar (ver fetchAllPages).
 *
 * ── Pré-requisitos no HTML ───────────────────────────────────────────────────
 *   1. div#product-grid-ajax com:
 *        data-id="{{ section.id }}"
 *        data-per-page="{{ section.settings.products_per_page }}"
 *        data-total-pages="{{ paginate.pages }}"
 *   2. div#collection-ajax — observado via MutationObserver para detectar
 *      re-renders após filtros/sort AJAX.
 *   3. div#pagination-ajax — onde a paginação virtual é injetada.
 *   4. Cada card (produto ou banner) dentro do grid com:
 *        data-product-available="{{ product.available }}" ("true"/"false")
 *      Cards sem esse atributo (ex.: banners) são tratados como disponíveis.
 *   5. Opcional: [data-collection-count] para exibir "Mostrando X–Y de Z".
 *
 * ── Comportamento ────────────────────────────────────────────────────────────
 * - Coleção com 1 página: reordena os cards diretamente no DOM (sem fetch).
 * - Coleção com múltiplas páginas: busca a página 1 (para confirmar o total
 *   real de páginas), depois busca o restante em paralelo, une os cards,
 *   ordena e constrói a paginação virtual client-side.
 * - Compatível com filtros e ordenação AJAX (detecta re-renders via
 *   MutationObserver e reinicia o sort automaticamente).
 * - Compatível com navegação via browser back/forward (popstate).
 * - Em caso de erro no fetch, cai no reorder de DOM como fallback.
 */

(function () {
  'use strict';

  // ─── Selectors (adapt these if the theme changes) ──────────────────────────
  var GRID_ID           = 'product-grid-ajax';
  var GRID_CONTAINER_ID = 'collection-ajax';
  var PAGINATION_SEL    = '#pagination-ajax';
  var MAX_PAGES         = 20;

  // ─── Observer handle — disconnected while this script writes to the DOM
  // itself, so its own renderPage()/reorderDOM() writes don't get picked up
  // as a "grid re-render" and re-trigger initVirtualSort() in a loop ──────────
  var _observer = null;

  function pauseObserverDuring(fn) {
    if (_observer) _observer.disconnect();
    fn();
    if (_observer) {
      var container = document.getElementById(GRID_CONTAINER_ID);
      if (container) _observer.observe(container, { childList: true, subtree: true });
    }
  }

  // ─── State ─────────────────────────────────────────────────────────────────
  var state = {
    cards        : [],   // { html, soldOut }[]
    perPage      : 8,
    sectionId    : '',
    totalPages   : 1,
    initialized  : false,
    fetching     : false,
    generation   : 0,
    arrowPrevHtml: '',
    arrowNextHtml: '',
  };

  // ─── Read metadata from #product-grid-ajax data attributes ────────────────
  function readMeta(root) {
    var el = (root || document).getElementById
      ? (root || document).getElementById(GRID_ID)
      : (root || document).querySelector('#' + GRID_ID);
    if (!el) return null;
    return {
      sectionId : el.dataset.id,
      perPage   : parseInt(el.dataset.perPage, 10)    || 24,
      totalPages: parseInt(el.dataset.totalPages, 10) || 1,
    };
  }

  // ─── URL builder — preserves existing filter/sort params ──────────────────
  function buildUrl(page, sectionId) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', page);
    params.set('section_id', sectionId);
    return window.location.pathname + '?' + params.toString();
  }

  function fetchPage(page, sectionId) {
    return fetch(buildUrl(page, sectionId)).then(function (r) { return r.text(); });
  }

  // ─── Fetch all server pages in parallel ───────────────────────────────────
  // Fetches page 1 first to read the TRUE total-pages count from the fresh
  // response (facets/sort AJAX never updates the live grid's own data
  // attributes, only its children), then fetches the rest in parallel.
  function fetchAllPages(sectionId, knownTotalPages) {
    return fetchPage(1, sectionId).then(function (firstHtml) {
      var doc = new DOMParser().parseFromString(firstHtml, 'text/html');
      var freshMeta = readMeta(doc);
      var totalPages = freshMeta ? freshMeta.totalPages : knownTotalPages;
      if (freshMeta) state.perPage = freshMeta.perPage;

      var count = Math.min(totalPages, MAX_PAGES);
      var fetches = [firstHtml];
      for (var i = 2; i <= count; i++) {
        fetches.push(fetchPage(i, sectionId));
      }
      return Promise.all(fetches);
    });
  }

  // ─── Extract cards from fetched HTML ───────────────────────────────────────
  function extractCards(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('#' + GRID_ID + ' > *')).map(function (card) {
      return {
        html   : card.outerHTML,
        soldOut: card.dataset.productAvailable === 'false',
      };
    });
  }

  // ─── Sort: available first, sold-out last ─────────────────────────────────
  function sortCards(cards) {
    var available = cards.filter(function (c) { return !c.soldOut; });
    var soldOut   = cards.filter(function (c) { return c.soldOut; });
    return available.concat(soldOut);
  }

  // ─── Capture arrow icons from the server-rendered pagination ─────────────
  function captureArrows() {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;
    var prevEl = wrapper.querySelector('.arrow-link--prev');
    var nextEl = wrapper.querySelector('.arrow-link--next');
    if (prevEl) state.arrowPrevHtml = prevEl.innerHTML;
    if (nextEl) state.arrowNextHtml = nextEl.innerHTML;
  }

  // ─── Update count text elements ───────────────────────────────────────────
  function updateCount(pageNum) {
    var total = state.cards.length;
    var start = (pageNum - 1) * state.perPage + 1;
    var end   = Math.min(pageNum * state.perPage, total);
    var text  = 'Mostrando ' + start + '–' + end + ' de ' + total + ' produtos';
    document.querySelectorAll('[data-collection-count]').forEach(function (el) {
      el.textContent = text;
    });
  }

  // ─── Render a page slice into the grid ────────────────────────────────────
  function renderPage(pageNum) {
    var grid = document.getElementById(GRID_ID);
    if (!grid || state.cards.length === 0) return;

    var start = (pageNum - 1) * state.perPage;
    var slice = state.cards.slice(start, start + state.perPage);
    if (slice.length === 0) return;

    pauseObserverDuring(function () {
      grid.innerHTML = slice.map(function (c) { return c.html; }).join('');

      // Reset animation order on inserted items
      Array.from(grid.children).forEach(function (card, i) {
        card.style.setProperty('--animation-order', i + 1);
        card.style.opacity = '1';
        card.removeAttribute('reveal');
      });
    });
  }

  // ─── Build virtual pagination (no page reload) ────────────────────────────
  function buildPagination(currentPage) {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;

    var totalPages = Math.ceil(state.cards.length / state.perPage);

    if (totalPages <= 1) {
      pauseObserverDuring(function () { wrapper.innerHTML = ''; });
      return;
    }

    var wideClass = totalPages > 5 ? ' wide-parts' : '';

    // Build numbered parts
    var parts = '';
    for (var i = 1; i <= totalPages; i++) {
      if (i === currentPage) {
        parts += '<span class="pagination-item current-page" aria-current="page"'
          + ' aria-label="Página ' + i + '"><span>' + i + '</span></span>';
      } else {
        parts += '<a href="#" class="pagination-item page-link"'
          + ' data-vs-page="' + i + '" aria-label="Página ' + i + '"><span>' + i + '</span></a>';
      }
    }

    // Build arrows
    var prevArrow, nextArrow;

    if (currentPage > 1) {
      prevArrow = '<a href="#" class="pagination-item arrow-link arrow-link--prev"'
        + ' data-vs-page="' + (currentPage - 1) + '" aria-label="Página anterior">'
        + (state.arrowPrevHtml || '&#8249;')
        + '</a>';
    } else {
      prevArrow = '<span class="pagination-item arrow-link arrow-link--prev disabled" aria-label="Página anterior">'
        + (state.arrowPrevHtml || '&#8249;')
        + '</span>';
    }

    if (currentPage < totalPages) {
      nextArrow = '<a href="#" class="pagination-item arrow-link arrow-link--next"'
        + ' data-vs-page="' + (currentPage + 1) + '" aria-label="Próxima página">'
        + (state.arrowNextHtml || '&#8250;')
        + '</a>';
    } else {
      nextArrow = '<span class="pagination-item arrow-link arrow-link--next disabled" aria-label="Próxima página">'
        + (state.arrowNextHtml || '&#8250;')
        + '</span>';
    }

    pauseObserverDuring(function () {
      wrapper.innerHTML = '<nav class="pagination mt-48 mb-48' + wideClass + ' size--tiny"'
        + ' role="navigation" aria-label="Paginação">'
        + prevArrow
        + '<div class="pagination-parts">' + parts + '</div>'
        + nextArrow
        + '</nav>';

      wrapper.dataset.page = currentPage;
    });
  }

  // ─── Navigate to a virtual page ───────────────────────────────────────────
  function navigateTo(pageNum) {
    renderPage(pageNum);
    buildPagination(pageNum);
    updateCount(pageNum);

    var url = new URL(window.location.href);
    if (pageNum === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', pageNum);
    }
    history.pushState({ vsPage: pageNum }, '', url.toString());

    // Scroll to top of grid container
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (container) {
      var top = container.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  // ─── Attach click handler to the virtual pagination ───────────────────────
  function attachPaginationListeners() {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;
    wrapper.removeEventListener('click', onPaginationClick);
    wrapper.addEventListener('click', onPaginationClick);
  }

  function onPaginationClick(e) {
    var link = e.target.closest('a[data-vs-page]');
    if (!link) return;
    e.preventDefault();
    var page = parseInt(link.dataset.vsPage, 10);
    if (!isNaN(page)) navigateTo(page);
  }

  // ─── In-page DOM reorder (fallback for single-page collections) ───────────
  function reorderDOM() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return;
    pauseObserverDuring(function () {
      var cards     = Array.from(grid.children);
      var available = cards.filter(function (card) { return card.dataset.productAvailable !== 'false'; });
      var soldOut   = cards.filter(function (card) { return card.dataset.productAvailable === 'false'; });
      available.concat(soldOut).forEach(function (card) { grid.appendChild(card); });
    });
  }

  // ─── Virtual sort orchestrator ────────────────────────────────────────────
  function initVirtualSort() {
    var meta = readMeta(document);
    if (!meta) return;

    state.sectionId  = meta.sectionId;
    state.perPage    = meta.perPage;
    state.totalPages = meta.totalPages;

    // Single-page collection: just reorder in DOM, no fetch needed
    if (meta.totalPages <= 1) {
      reorderDOM();
      return;
    }

    var gen = ++state.generation;
    state.fetching    = false;
    state.initialized = false;
    state.fetching    = true;

    // Capture arrows before we replace server-rendered pagination
    captureArrows();

    // Hide grid to prevent flash of unsorted content
    var grid = document.getElementById(GRID_ID);
    if (grid) grid.style.visibility = 'hidden';

    fetchAllPages(state.sectionId, state.totalPages)
      .then(function (pages) {
        if (gen !== state.generation) return; // Stale — a newer sort was triggered

        var all = [];
        pages.forEach(function (html) {
          all = all.concat(extractCards(html));
        });

        state.cards       = sortCards(all);
        state.fetching    = false;
        state.initialized = true;

        renderPage(1);
        buildPagination(1);
        updateCount(1);
        attachPaginationListeners();

        if (grid) grid.style.visibility = '';
      })
      .catch(function (err) {
        console.warn('[sort-availability] Fetch failed, falling back to in-page sort.', err);
        if (gen !== state.generation) return;
        state.fetching = false;
        if (grid) grid.style.visibility = '';
        reorderDOM();
      });
  }

  // ─── MutationObserver — detect facets/sort AJAX updates ───────────────────
  var _debounceTimer = null;

  function watchForUpdates() {
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (!container) return;

    _observer = new MutationObserver(function (mutations) {
      // React ONLY when #product-grid-ajax's OWN child list changes (target
      // is the grid element itself — this theme's AJAX pattern of swapping
      // its innerHTML), or when the grid element itself gets (re)inserted
      // somewhere in the subtree (Dawn-style whole-element swap).
      //
      // Do NOT match on descendants-of-grid in general (e.g. a mutation
      // whose target is a card inside the grid) — third-party apps (like
      // RestockRocket) inject buttons into each card via their own script,
      // which is a legitimate mutation *inside* the grid but is NOT a
      // facets/sort re-render, and reacting to it caused an infinite loop:
      // our reinit re-renders the grid → the app re-injects its button →
      // we reinit again, forever.
      var gridInserted = mutations.some(function (m) {
        if (m.target && m.target.nodeType === 1 && m.target.id === GRID_ID) {
          return true;
        }
        return Array.from(m.addedNodes).some(function (n) {
          return n.nodeType === 1 && n.id === GRID_ID;
        });
      });

      if (!gridInserted || state.fetching) return;

      // Debounce: wait for URL (history.pushState) to update after DOM change
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () {
        state.cards       = [];
        state.initialized = false;
        initVirtualSort();
      }, 150);
    });

    _observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Handle browser back/forward navigation ───────────────────────────────
  window.addEventListener('popstate', function (e) {
    if (state.initialized && e.state && e.state.vsPage) {
      renderPage(e.state.vsPage);
      buildPagination(e.state.vsPage);
    }
  });

  // ─── Boot ─────────────────────────────────────────────────────────────────
  function boot() {
    if (!document.getElementById(GRID_ID)) return;
    initVirtualSort();
    watchForUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
